import { getSiteBrand } from "@/lib/siteHosts"

function privacyBlocks() {
  const brand = getSiteBrand() || {}
  const operator = brand.legalName || brand.name || "The company that publishes this site"
  const address = typeof brand.address === "string" ? brand.address.trim() : ""
  const email = typeof brand.supportEmail === "string" ? brand.supportEmail.trim() : ""
  const blocks = [
    { type: "paragraph", text: "Last updated: October 18, 2026." },
    { type: "heading", text: "This page only" },
    {
      type: "paragraph",
      text: "This Privacy Policy applies only to the Roofing Speed-to-Lead Benchmark Report at /lp/roofing-speed-to-lead-benchmark-report. It does not apply to other pages on this site, to accounts, or to any other product.",
    },
    { type: "heading", text: "Who operates this page" },
    {
      type: "paragraph",
      text: address
        ? `This page is operated by ${operator} (${address}).`
        : `This page is operated by ${operator}.`,
    },
    { type: "heading", text: "Company search" },
    {
      type: "paragraph",
      text: "If you search the audit database, the company name you type is sent to the benchmark service so it can return matching published results, such as a business name, location, and measured response time. The leaderboard loads those published records. It does not send your name or contact details. The search box in the hero does not send what you type.",
    },
    { type: "heading", text: "Country" },
    {
      type: "paragraph",
      text: "If the page address does not already include a country, the page asks our server for a two-letter country code. The server reads that code from the Cloudflare country header on the request or, when that header is absent, from a lookup of the IP address through ip-api.com. The page uses the code for this visit. This page does not build a visitor profile from it.",
    },
    { type: "heading", text: "What stays in the browser" },
    {
      type: "paragraph",
      text: "The audit request form (name, website, phone number, and email), the missing-company form (website domain and phone number), and the calculator keep what you type in your browser. Submitting them on this page does not send those fields to us.",
    },
    { type: "heading", text: "Measurement" },
    {
      type: "paragraph",
      text: "This page loads Google Tag Manager and PostHog. They may store a cookie or a similar identifier and receive technical data about the visit, such as the page address, the browser, and how you use the page. PostHog may record the session. This page does not identify you to those tools by name or email.",
    },
    { type: "heading", text: "How long it is kept" },
    {
      type: "paragraph",
      text: "What stays in your browser is gone when you close or refresh the page, unless your browser keeps it. The company name is sent to return search results. The country code is used for the visit. Google Tag Manager, PostHog, and ip-api.com keep what they receive under their own practices. This page does not sell personal information.",
    },
    { type: "heading", text: "Questions" },
    {
      type: "paragraph",
      text: "This page is meant for roofing businesses. It is not directed at children. To ask about this policy or about information handled on this page, email us.",
    },
  ]
  if (email) {
    blocks.push({ type: "mailto", label: email, href: `mailto:${email}` })
  }
  return blocks
}

function termsBlocks() {
  const brand = getSiteBrand() || {}
  const operator = brand.legalName || brand.name || "The company that publishes this site"
  const address = typeof brand.address === "string" ? brand.address.trim() : ""
  const email = typeof brand.supportEmail === "string" ? brand.supportEmail.trim() : ""
  const law = typeof brand.governingLaw === "string" ? brand.governingLaw.trim() : ""
  const blocks = [
    { type: "paragraph", text: "Last updated: October 18, 2026." },
    { type: "heading", text: "This page only" },
    {
      type: "paragraph",
      text: "These Terms of Service apply only to your use of the Roofing Speed-to-Lead Benchmark Report at /lp/roofing-speed-to-lead-benchmark-report. They do not apply to other pages on this site, to accounts, or to any other product. Using this page means you accept these terms.",
    },
    { type: "heading", text: "Who offers this page" },
    {
      type: "paragraph",
      text: address
        ? `This page is offered by ${operator} (${address}).`
        : `This page is offered by ${operator}.`,
    },
    { type: "heading", text: "What this page is" },
    {
      type: "paragraph",
      text: "This page publishes a speed-to-lead benchmark for roofing businesses, including a company search, a leaderboard, and a calculator. It does not open an account, take a payment, or start an engagement.",
    },
    { type: "heading", text: "The figures" },
    {
      type: "paragraph",
      text: "Response times, leakage figures, and calculator results are research illustrations. The calculator uses the numbers you type and the assumptions shown on the page. Those results are not a quote, a forecast of your revenue, or a promise that your business will match the benchmark.",
    },
    { type: "heading", text: "Search results" },
    {
      type: "paragraph",
      text: "A search returns published audit records when the benchmark service has a match. A listed record can be incomplete or out of date. A missing record does not mean a company was never audited. A listing is not an endorsement or a criticism of that business.",
    },
    { type: "heading", text: "Forms on this page" },
    {
      type: "paragraph",
      text: "The audit request form, the missing-company form, and the search box in the hero do not send what you type and do not create a request, an order, or a contract. Submitting them does not start an audit and does not mean we will contact you.",
    },
    { type: "heading", text: "The report" },
    {
      type: "paragraph",
      text: "The text, layout, and compiled findings on this page belong to the operator. You may read them here. You may not copy the compiled report or republish it as your own.",
    },
    { type: "heading", text: "Availability" },
    {
      type: "paragraph",
      text: "The page is provided as it is available. We may change or remove it. We do not promise that the page, the search, or the leaderboard will be uninterrupted or error-free.",
    },
    { type: "heading", text: "Decisions you make" },
    {
      type: "paragraph",
      text: "You are responsible for decisions you make from what you read here. To the extent the law allows, the operator is not liable for loss that results from relying on the benchmark, the search, or the calculator.",
    },
    { type: "heading", text: "Privacy" },
    {
      type: "paragraph",
      text: "Information handled on this page is described in the Privacy Policy opened from this page. These terms do not replace that policy.",
    },
  ]
  if (law) {
    blocks.push({ type: "heading", text: "Law" })
    blocks.push({
      type: "paragraph",
      text: `These terms are governed by the laws of ${law}.`,
    })
  }
  blocks.push({ type: "heading", text: "Questions" })
  blocks.push({
    type: "paragraph",
    text: "To ask about these terms, email us.",
  })
  if (email) {
    blocks.push({ type: "mailto", label: email, href: `mailto:${email}` })
  }
  return blocks
}

function contactBlocks() {
  const brand = getSiteBrand() || {}
  const operator = brand.legalName || brand.name || "The company that publishes this site"
  const address = typeof brand.address === "string" ? brand.address.trim() : ""
  const email = typeof brand.supportEmail === "string" ? brand.supportEmail.trim() : ""
  const blocks = [
    { type: "paragraph", text: "Last updated: October 18, 2026." },
    { type: "heading", text: "This page only" },
    {
      type: "paragraph",
      text: "This note is only for questions about the Roofing Speed-to-Lead Benchmark Report at /lp/roofing-speed-to-lead-benchmark-report. It is not the contact channel for other pages, accounts, or products.",
    },
    { type: "heading", text: "Who to write" },
    {
      type: "paragraph",
      text: address
        ? `Write to ${operator} (${address}), the operator of this page.`
        : `Write to ${operator}, the operator of this page.`,
    },
    { type: "heading", text: "Email" },
    {
      type: "paragraph",
      text: "For a question about the report, a search result, the Privacy Policy, or the Terms of Service on this page, email us.",
    },
  ]
  if (email) {
    blocks.push({ type: "mailto", label: email, href: `mailto:${email}` })
  }
  blocks.push({ type: "heading", text: "Forms on this page" })
  blocks.push({
    type: "paragraph",
    text: "The audit request form, the missing-company form, and the search box in the hero do not send a message. They are not a way to reach us.",
  })
  return blocks
}

export const legalDocuments = {
  privacy: {
    title: "Privacy Policy",
    blocks: privacyBlocks(),
  },
  terms: {
    title: "Terms of Service",
    blocks: termsBlocks(),
  },
  contact: {
    title: "Contact Us",
    blocks: contactBlocks(),
  },
}
